import rawManifest from './publicPageMeta.json';

/**
 * Supported Open Graph `og:type` values. `website` is the default for all
 * regular pages; long-form documentation pages (Tutorial, API docs, blog
 * posts, etc.) should opt in to `article`, which also unlocks
 * `article:published_time` / `article:modified_time`.
 */
export type OgType = 'website' | 'article';

/**
 * Every publicly crawlable route. Kept as a literal union so that a typo in a
 * `getPublicPageMeta()` call is a compile error rather than a runtime surprise,
 * and so adding a public page forces a matching manifest entry.
 */
export type PublicPagePath =
  | '/'
  | '/sheet-music-scanner'
  | '/pricing'
  | '/tutorial'
  | '/business-api'
  | '/support'
  | '/privacy-policy'
  | '/terms-of-service'
  | '/sample/bach-prelude-in-c-major-bwv-846'
  | '/sample/chopin-nocturne-op-9-no-2';

const PUBLIC_PAGE_PATHS: readonly PublicPagePath[] = [
  '/',
  '/sheet-music-scanner',
  '/pricing',
  '/tutorial',
  '/business-api',
  '/support',
  '/privacy-policy',
  '/terms-of-service',
  '/sample/bach-prelude-in-c-major-bwv-846',
  '/sample/chopin-nocturne-op-9-no-2',
];

export interface PublicPageMeta {
  title: string;
  description: string;
  path: PublicPagePath;
  /** Absolute OG/Twitter image URL. Defaults to the site-wide image. */
  image?: string;
  ogType?: OgType;
  articleModifiedTime?: string;
}

/**
 * Shape of `publicPageMeta.json`. Declared explicitly (rather than relying on
 * the inferred JSON type) because the pages array is heterogeneous — only
 * `article` pages carry `ogType` / `articleModifiedTime` — and the inferred
 * union would make those fields inaccessible without narrowing.
 */
interface RawPage {
  path: string;
  title: string;
  description: string;
  ogType?: string;
  /**
   * Date the page's content last meaningfully changed, as `YYYY-MM-DD`. Drives
   * both the sitemap's `<lastmod>` and — on `article` pages —
   * `article:modified_time`, so there is only one date per page to maintain.
   * Bump it when you edit that page's copy; leaving it stale is better than
   * setting it to the build date, because a sitemap where every URL changed
   * today is a signal crawlers learn to ignore.
   */
  lastModified?: string;
  /** Site-relative path, e.g. `/demo/bwv846/og.png`. */
  image?: string;
}

interface RawManifest {
  siteUrl: string;
  siteName: string;
  defaultImagePath: string;
  pages: RawPage[];
}

const manifest = rawManifest as unknown as RawManifest;

export const SITE_URL = manifest.siteUrl;
export const SITE_NAME = manifest.siteName;
export const DEFAULT_IMAGE = `${manifest.siteUrl}${manifest.defaultImagePath}`;

const isOgType = (value: unknown): value is OgType =>
  value === 'website' || value === 'article';

const META_BY_PATH = new Map<string, PublicPageMeta>(
  manifest.pages.map((page) => [
    page.path,
    {
      title: page.title,
      description: page.description,
      path: page.path as PublicPagePath,
      ...(page.image
        ? {
            image: page.image.startsWith('http')
              ? page.image
              : `${manifest.siteUrl}${page.image}`,
          }
        : {}),
      ...(isOgType(page.ogType) ? { ogType: page.ogType } : {}),
      // `article:modified_time` is only emitted for article pages, so the
      // shared `lastModified` date is only surfaced to `usePageMeta` there.
      ...(page.ogType === 'article' && page.lastModified
        ? { articleModifiedTime: page.lastModified }
        : {}),
    },
  ])
);

// Fail fast at module load rather than mid-render: the manifest and the
// `PublicPagePath` union are both checked into this repo and built together,
// so a mismatch is an authoring mistake that should surface immediately in dev
// and CI, not as a blank page in production.
const missingPaths = PUBLIC_PAGE_PATHS.filter(
  (path) => !META_BY_PATH.has(path)
);
if (missingPaths.length > 0) {
  throw new Error(
    `publicPageMeta.json is missing entries for: ${missingPaths.join(', ')}`
  );
}

/**
 * Page metadata for a public route, shared by the React pages (via
 * `usePageMeta`) and by the build-time prerenderer
 * (`scripts/prerender-public-pages.js`), which reads the same JSON manifest.
 * Keeping one source of truth is what stops the static HTML `<head>` and the
 * client-injected `<head>` from drifting apart.
 */
export const getPublicPageMeta = (path: PublicPagePath): PublicPageMeta =>
  META_BY_PATH.get(path) as PublicPageMeta;

/** Look up prerendered meta for `/sample/:slug`, or `undefined` if unknown. */
export const getSamplePageMeta = (
  slug: string | undefined
): PublicPageMeta | undefined =>
  slug ? META_BY_PATH.get(`/sample/${slug}`) : undefined;
